import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, Key, RefreshCw, Copy, Check, Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useOrganization } from "@/hooks/useOrganization";

export function WebhookSettings() {
  const { toast } = useToast();
  const { organization } = useOrganization();
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch current webhook secret
  const { data: secretData, isLoading, refetch } = useQuery({
    queryKey: ['webhook-secret', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return null;
      
      const { data, error } = await supabase
        .from('organization_settings')
        .select('value')
        .eq('org_id', organization.id)
        .eq('key', 'webhook_secret')
        .single();
      
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      return data?.value || null;
    },
    enabled: !!organization?.id
  });

  useEffect(() => {
    if (secretData) {
      setWebhookSecret(secretData);
    }
  }, [secretData]);

  // Generate new secret
  const generateSecret = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const secret = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    setWebhookSecret(secret);
  };

  // Save webhook secret
  const saveMutation = useMutation({
    mutationFn: async (secret: string) => {
      if (!organization?.id) {
        throw new Error('No organization selected');
      }

      // Check if setting exists
      const { data: existing } = await supabase
        .from('organization_settings')
        .select('id')
        .eq('org_id', organization.id)
        .eq('key', 'webhook_secret')
        .single();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('organization_settings')
          .update({ value: secret })
          .eq('org_id', organization.id)
          .eq('key', 'webhook_secret');
        
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('organization_settings')
          .insert({
            org_id: organization.id,
            key: 'webhook_secret',
            value: secret,
            description: 'Default webhook HMAC secret key for signature generation'
          });
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Webhook secret saved successfully",
      });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to save webhook secret",
        variant: "destructive",
      });
    }
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(webhookSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: "Copied",
        description: "Webhook secret copied to clipboard",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  if (!organization) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Please select an organization to manage webhook settings.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Key className="h-5 w-5 text-primary" />
          <CardTitle>Webhook Security Settings</CardTitle>
        </div>
        <CardDescription>
          Manage the default webhook secret key used for HMAC-SHA256 signature generation.
          This secret is used for all webhook templates that don't have their own secret key configured.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            <strong>Important:</strong> Keep your webhook secret secure. Recipients should use this secret 
            to validate webhook signatures and ensure requests originate from your system.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="webhook_secret">Organization Webhook Secret</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="webhook_secret"
                  type={showSecret ? "text" : "password"}
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder="Enter or generate a webhook secret"
                  className="font-mono text-sm pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-7 w-7 p-0"
                  onClick={() => setShowSecret(!showSecret)}
                >
                  {showSecret ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleCopy}
                disabled={!webhookSecret}
                title="Copy to clipboard"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              A secure random string used to sign webhook payloads. Minimum 32 characters recommended.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={generateSecret}
              className="flex-1"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Generate New Secret
            </Button>
            <Button
              type="button"
              onClick={() => saveMutation.mutate(webhookSecret)}
              disabled={!webhookSecret || webhookSecret.length < 16 || saveMutation.isPending}
              className="flex-1"
            >
              {saveMutation.isPending ? "Saving..." : "Save Secret"}
            </Button>
          </div>

          {webhookSecret && webhookSecret.length < 32 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Secret should be at least 32 characters for adequate security.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="pt-4 border-t space-y-3">
          <h4 className="text-sm font-semibold">Signature Verification Guide</h4>
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <p className="text-xs text-muted-foreground">
              Recipients should validate webhooks using this algorithm:
            </p>
            <pre className="text-xs font-mono bg-background p-3 rounded border overflow-x-auto">
{`// Extract headers
const timestamp = headers['x-webhook-timestamp'];
const signature = headers['x-webhook-signature'];

// Recreate signature string
const signatureString = timestamp + '.' + JSON.stringify(payload);

// Calculate expected signature
const expectedSignature = crypto
  .createHmac('sha256', webhookSecret)
  .update(signatureString)
  .digest('hex');

// Verify signatures match
if (signature !== expectedSignature) {
  throw new Error('Invalid signature');
}

// Verify timestamp (prevent replay attacks)
const age = Date.now() - parseInt(timestamp);
if (age > 300000) { // 5 minutes
  throw new Error('Webhook too old');
}`}</pre>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
